import React, {
    FormEvent,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import { Button, TextField, TextFieldProps, Typography } from "@mui/material";
import clsx from "clsx";
import { DEFAULT_POLL_TEXT_FIELD_SCHEMA } from "@Interfaces/Schemas/PollTextFieldSchema";

/**
 * TextFieldProps minus the controlled props we manage internally so callers can still tweak the underlying input.
 */
type PollTextFieldTextFieldProps = Omit<
    TextFieldProps,
    | "onChange"
    | "value"
    | "rows"
    | "multiline"
    | "fullWidth"
    | "placeholder"
    | "error"
    | "helperText"
    | "disabled"
    | "aria-label"
>;

export interface PollTextFieldProps {
    question: string;
    onSubmit: (response: string) => void | Promise<void>;
    placeholder?: string;
    helperText?: string;
    submitLabel?: string;
    required?: boolean;
    maxLength?: number;
    rows?: number;
    successMessage?: string;
    validateEmptyMessage?: string;
    validateLengthMessage?: string;
    disabled?: boolean;
    initialValue?: string;
    className?: string;
    formClassName?: string;
    promptClassName?: string;
    inputClassName?: string;
    actionsClassName?: string;
    counterClassName?: string;
    counterFormatter?: (count: number, limit?: number) => string;
    textFieldProps?: PollTextFieldTextFieldProps;
    onChange?: (value: string) => void;
}

// Stateless component that centralizes the presentation and validation of a respondent-facing textfield.
export const PollTextField: React.FC<PollTextFieldProps> = ({
    question,
    onSubmit,
    placeholder,
    helperText,
    submitLabel = "Submit",
    required = DEFAULT_POLL_TEXT_FIELD_SCHEMA.required,
    maxLength = DEFAULT_POLL_TEXT_FIELD_SCHEMA.maxLength,
    rows = DEFAULT_POLL_TEXT_FIELD_SCHEMA.rows,
    successMessage,
    validateEmptyMessage = "This field is required.",
    validateLengthMessage,
    disabled = false,
    initialValue,
    className,
    formClassName,
    promptClassName,
    inputClassName,
    actionsClassName,
    counterClassName,
    counterFormatter,
    textFieldProps,
    onChange,
}) => {
    const [value, setValue] = useState(initialValue ?? "");
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [statusMessage, setStatusMessage] = useState<string | null>(null);

    useEffect(() => {
        setValue(initialValue ?? "");
    }, [initialValue]);

    // Determine whether the submit button should be disabled (e.g., while submitting or when required input is empty).
    const isButtonDisabled = useMemo(() => {
        const trimmedValue = value.trim();
        return (
            disabled || isSubmitting || (required && trimmedValue.length === 0)
        );
    }, [disabled, isSubmitting, required, value]);

    const getCounterText = useCallback(() => {
        if (!maxLength) return "";
        const charCount = value?.length ?? 0;
        if (typeof counterFormatter === "function") {
            return counterFormatter(charCount, maxLength);
        }
        return `${charCount}/${maxLength}`;
    }, [counterFormatter, maxLength, value]);

    // Clear errors/status and lift changes when the user edits the input.
    const handleChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const nextValue = event.target.value;
            setValue(nextValue);
            if (onChange) {
                onChange(nextValue);
            }
            if (error) {
                setError(null);
            }
            if (statusMessage) {
                setStatusMessage(null);
            }
        },
        [error, onChange, statusMessage]
    );

    // Validate the text and delegate the submission to the caller (supports async handlers).
    const handleSubmit = useCallback(
        async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (isButtonDisabled) {
                return;
            }

            const trimmed = value.trim();
            if (required && trimmed.length === 0) {
                setError(validateEmptyMessage);
                return;
            }

            if (maxLength && trimmed.length > maxLength) {
                setError(
                    validateLengthMessage ??
                        `Max length is ${maxLength} characters.`
                );
                return;
            }

            setError(null);
            setIsSubmitting(true);

            try {
                await Promise.resolve(onSubmit(trimmed));
                setStatusMessage(successMessage ?? null);
                setValue("");
                if (onChange) {
                    onChange("");
                }
            } catch (submitError) {
                const errorMessage =
                    submitError instanceof Error
                        ? submitError.message
                        : "Unable to submit data.";
                setError(errorMessage);
            } finally {
                setIsSubmitting(false);
            }
        },
        [
            isButtonDisabled,
            maxLength,
            onChange,
            onSubmit,
            required,
            successMessage,
            validateEmptyMessage,
            validateLengthMessage,
            value,
        ]
    );

    const {
        InputProps: additionalInputProps = {},
        inputProps: additionalNativeInputProps = {},
        ...restTextFieldProps
    } = textFieldProps ?? {};

    // Extend any consumer-provided InputProps while attaching our class names for styling.
    const mergedInputProps = {
        ...additionalInputProps,
        classes: {
            ...(additionalInputProps.classes || {}),
            input: clsx(additionalInputProps.classes?.input, inputClassName),
        },
    };

    return (
        <form
            className={clsx(formClassName, className)}
            onSubmit={handleSubmit}
            noValidate
            aria-label={question}
        >
            <Typography variant="subtitle1" className={promptClassName}>
                {question}
            </Typography>
            <TextField
                {...restTextFieldProps}
                fullWidth
                multiline
                rows={rows}
                variant={restTextFieldProps.variant ?? "standard"}
                placeholder={placeholder}
                value={value}
                onChange={handleChange}
                error={Boolean(error)}
                disabled={disabled || isSubmitting}
                inputProps={{
                    ...additionalNativeInputProps,
                    maxLength: maxLength ?? undefined,
                }}
                InputProps={mergedInputProps}
                helperText={null}
            />
            <div className={actionsClassName}>
                <Typography
                    variant="caption"
                    color={
                        error
                            ? "error"
                            : statusMessage
                            ? "primary"
                            : "textSecondary"
                    }
                    className={counterClassName}
                    aria-live="polite"
                >
                    {error || statusMessage || getCounterText()}
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={isButtonDisabled}
                >
                    {submitLabel}
                </Button>
            </div>
            {helperText && !error && !statusMessage && (
                <Typography variant="caption" color="textSecondary">
                    {helperText}
                </Typography>
            )}
        </form>
    );
};

export default PollTextField;
